import { Box, Menu, MenuItem, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { AGENT_LANGUAGE_OPTIONS } from 'components/@home/@settings/Tabs/Agents/agentLanguageOptions';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import StarIcon from 'mdi-react/StarIcon';
import StarOutlineIcon from 'mdi-react/StarOutlineIcon';
import { func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const AgentLanguagesCard = ({ draft, onChange }) => {
  const theme = useTheme();
  const [addAnchor, setAddAnchor] = useState(null);

  const enabled = useMemo(() => {
    const raw = Array.isArray(draft.supportedLocales) ? draft.supportedLocales : [];
    const primary = draft.locale || 'es';
    const merged = [primary, ...raw.filter(l => l !== primary)];
    return merged.length ? merged : [primary];
  }, [draft.locale, draft.supportedLocales]);

  const primary = draft.locale || enabled[0];

  const available = useMemo(
    () => AGENT_LANGUAGE_OPTIONS.filter(o => !enabled.includes(o.code)),
    [enabled],
  );

  const commit = (nextEnabled, nextPrimary) => {
    const unique = [...new Set(nextEnabled)];
    let nextLocale = nextPrimary;
    if (!unique.includes(nextLocale)) {
      nextLocale = unique[0] || 'es';
    }
    onChange({ locale: nextLocale, supportedLocales: unique });
  };

  const setPrimary = code => commit(enabled, code);

  const addLanguage = code => {
    setAddAnchor(null);
    commit([...enabled, code], primary);
  };

  const removeLanguage = code => {
    if (enabled.length === 1) return;
    const next = enabled.filter(l => l !== code);
    commit(next, code === primary ? next[0] : primary);
  };

  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    py: 0.75,
    pl: 1.25,
    pr: 1,
    borderRadius: '999px',
    border: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  };

  const chipPrimarySx = {
    ...chipSx,
    borderColor: 'primary.main',
    bgcolor: primaryTint(theme, 0.06),
  };

  const starBtnSx = {
    display: 'grid',
    placeItems: 'center',
    p: 0,
    border: 'none',
    bgcolor: 'transparent',
    cursor: 'pointer',
    color: 'text.secondary',
  };

  const codeChipSx = {
    flex: '0 0 auto',
    minWidth: 28,
    height: 22,
    px: 0.75,
    borderRadius: '5px',
    display: 'grid',
    placeItems: 'center',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    bgcolor: 'action.hover',
    color: 'primary.main',
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: t => t.typography.pxToRem(16),
          fontWeight: 700,
          color: 'text.primary',
          mb: 2,
        }}
      >
        {I18n.t('AgenticChatFlow.LanguagesTitle')}
      </Typography>
      <Box sx={{ height: '1px', bgcolor: 'divider', mb: 2.5 }} />
      <Typography
        sx={{
          fontSize: t => t.typography.pxToRem(12.5),
          color: 'text.secondary',
          lineHeight: 1.5,
          maxWidth: 620,
          mb: 2.5,
        }}
      >
        {I18n.t('AgenticChatFlow.LanguagesIntro')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, maxWidth: 620 }}>
        {enabled.map(code => {
          const opt = AGENT_LANGUAGE_OPTIONS.find(o => o.code === code);
          const isPrimary = code === primary;
          const name = opt ? I18n.t(opt.labelKey) : code;
          return (
            <Box key={code} sx={isPrimary ? chipPrimarySx : chipSx}>
              <Tooltip
                title={I18n.t(
                  isPrimary
                    ? 'AgenticChatFlow.LanguagesPrimaryTag'
                    : 'AgenticChatFlow.LanguagesMakePrimary',
                )}
              >
                <Box
                  component="button"
                  type="button"
                  sx={[starBtnSx, isPrimary && { color: 'primary.main' }]}
                  onClick={() => setPrimary(code)}
                  aria-label={I18n.t('AgenticChatFlow.LanguagesMakePrimary')}
                >
                  {isPrimary ? <StarIcon size={16} /> : <StarOutlineIcon size={16} />}
                </Box>
              </Tooltip>
              {opt?.chip && (
                <Box component="span" sx={codeChipSx}>
                  {opt.chip}
                </Box>
              )}
              <Box
                component="span"
                sx={{
                  fontSize: t => t.typography.pxToRem(13),
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {name}
              </Box>
              {isPrimary && (
                <Box
                  component="span"
                  sx={{
                    fontSize: t => t.typography.pxToRem(10),
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    color: 'primary.main',
                    textTransform: 'uppercase',
                  }}
                >
                  {I18n.t('AgenticChatFlow.LanguagesPrimaryTag')}
                </Box>
              )}
              <Box
                component="button"
                type="button"
                disabled={enabled.length === 1}
                onClick={() => removeLanguage(code)}
                aria-label={`${I18n.t('AgenticChatFlow.LanguagesRemove')} ${name}`}
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 18,
                  height: 18,
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                  '&:disabled': { color: 'text.disabled', cursor: 'default' },
                }}
              >
                <CloseIcon size={15} />
              </Box>
            </Box>
          );
        })}

        <Box
          component="button"
          type="button"
          disabled={available.length === 0}
          onClick={e => setAddAnchor(e.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            py: 0.75,
            pl: 1.25,
            pr: 1.75,
            borderRadius: '999px',
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'text.disabled',
            bgcolor: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: t => t.typography.pxToRem(13),
            fontWeight: 500,
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
            '&:disabled': {
              color: 'text.disabled',
              borderColor: 'divider',
              cursor: 'default',
            },
          }}
        >
          <PlusIcon size={15} />
          {I18n.t('AgenticChatFlow.LanguagesAdd')}
        </Box>
        <Menu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          onClose={() => setAddAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {available.map(opt => (
            <MenuItem
              key={opt.code}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              onClick={() => addLanguage(opt.code)}
            >
              {opt.chip && (
                <Box component="span" sx={codeChipSx}>
                  {opt.chip}
                </Box>
              )}
              <span>{I18n.t(opt.labelKey)}</span>
              {opt.native && (
                <Box
                  component="span"
                  sx={{
                    ml: 'auto',
                    color: 'text.secondary',
                    fontSize: t => t.typography.pxToRem(12),
                  }}
                >
                  {opt.native}
                </Box>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.75,
          alignItems: 'flex-start',
          maxWidth: 620,
          mt: 3,
        }}
      >
        <Switch
          size="small"
          checked={draft.autoSwitchLanguage !== false}
          onChange={e => onChange({ autoSwitchLanguage: e.target.checked })}
          slotProps={{ input: { 'aria-label': I18n.t('AgenticChatFlow.LanguagesAutoSwitch') } }}
        />
        <Stack spacing={0}>
          <Typography
            sx={{
              fontSize: t => t.typography.pxToRem(13),
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            {I18n.t('AgenticChatFlow.LanguagesAutoSwitch')}
          </Typography>
          <Typography
            sx={{
              fontSize: t => t.typography.pxToRem(12),
              color: 'text.secondary',
              lineHeight: 1.5,
              maxWidth: 460,
              mt: 0.5,
            }}
          >
            {I18n.t('AgenticChatFlow.LanguagesAutoSwitchHelper')}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

AgentLanguagesCard.propTypes = {
  draft: object.isRequired,
  onChange: func.isRequired,
};

export default AgentLanguagesCard;
